import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog } from '@tom-ui/ui'
import { getAllErrorDescriptions } from '@tom-ui/utils'
import { BlockAccessPointDto } from '@yard/app/api'
import { observer } from 'mobx-react-lite'
import { FC, useState } from 'react'
import { YardBlockViewStore } from '../../stores/nodes/yard-block.view-store'
import { BlockAccessPointForm, BlockAccessPointFormValues } from '../molecules/BlockAccessPointForm'

interface Props {
  yardBlockViewStore: YardBlockViewStore
}

const FORM_ID = 'block-access-point-form'

export const BlockAccessPointsPanel: FC<Props> = observer(({ yardBlockViewStore }) => {
  const { t } = useTranslate()
  const [apiError, setApiError] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<BlockAccessPointDto | null>(null)

  const block = yardBlockViewStore.accessPoints.accessPointDialogBlock
  const mode = yardBlockViewStore.accessPoints.accessPointDialogMode
  const editId = yardBlockViewStore.accessPoints.accessPointDialogEditId
  const editingAp = block?.accessPoints?.find(ap => ap.id === editId) ?? null

  if (!block) return null

  const handleClose = () => {
    setApiError(null)
    yardBlockViewStore.accessPoints.closeAccessPointDialog()
  }

  const handleSubmit = async (values: BlockAccessPointFormValues) => {
    if (!block) return
    try {
      setApiError(null)
      if (mode === 'add') {
        const isHorizontal = values.side === 'North' || values.side === 'South'
        const axisLength = isHorizontal ? block.bayNames.length : block.rowNames.length
        const rawMin = isHorizontal ? values.minBay : values.minRow
        const rawMax = isHorizontal ? values.maxBay : values.maxRow
        const effMin = rawMin ?? 1
        const effMax = rawMax ?? axisLength
        const shouldSplit = values.createPerItem && effMax > effMin

        const items = shouldSplit
          ? Array.from({ length: effMax - effMin + 1 }, (_, k) => ({
              minBay: isHorizontal ? effMin + k : null,
              maxBay: isHorizontal ? effMin + k : null,
              minRow: !isHorizontal ? effMin + k : null,
              maxRow: !isHorizontal ? effMin + k : null,
            }))
          : [
              {
                minBay: values.minBay,
                maxBay: values.maxBay,
                minRow: values.minRow,
                maxRow: values.maxRow,
              },
            ]

        await yardBlockViewStore.accessPoints.createAccessPoints({
          blockId: block.id,
          side: values.side,
          items,
        })
      } else if (mode === 'edit' && editingAp) {
        const { createPerItem: _ignored, ...request } = values
        await yardBlockViewStore.accessPoints.updateAccessPoint(block.id, {
          id: editingAp.id,
          ...request,
        })
      }
      handleClose()
    } catch (e: any) {
      const errorDescriptions = getAllErrorDescriptions(e)
      setApiError(
        errorDescriptions?.[0]?.payload?.message ?? t('unknownError', 'An error occurred'),
      )
    }
  }

  const handleDeleteConfirm = async () => {
    if (!block || !deleteTarget) return
    try {
      await yardBlockViewStore.accessPoints.deleteAccessPoint(deleteTarget.id, block.id)
      setDeleteTarget(null)
      handleClose()
    } catch {
      // optimistic rollback already handled in store
    }
  }

  const isOpen =
    yardBlockViewStore.accessPoints.isAccessPointDialogOpen && (mode === 'add' || mode === 'edit')
  const title =
    mode === 'add'
      ? t('addAccessPoint', 'Add access point for {block}', { block: block.name })
      : t('editAccessPoint', 'Edit access point for {block}', { block: block.name })

  return (
    <>
      <ConfirmationDialog
        open={isOpen}
        title={title}
        maxWidth='xs'
        formId={FORM_ID}
        primaryActionText={t('save', 'Save')}
        closeLabel={t('cancel', 'Cancel')}
        secondaryActionText={mode === 'edit' && editingAp ? t('delete', 'Delete') : undefined}
        secondaryActionColor='error'
        onSecondaryAction={
          mode === 'edit' && editingAp ? () => setDeleteTarget(editingAp) : undefined
        }
        onClose={handleClose}
        onConfirm={() => {
          return
        }}
      >
        <BlockAccessPointForm
          id={FORM_ID}
          initialValues={editingAp ?? undefined}
          apiError={apiError}
          bayNames={block.bayNames}
          rowNames={block.rowNames}
          showCreatePerItem={mode === 'add'}
          onSubmit={handleSubmit}
        />
      </ConfirmationDialog>

      <ConfirmationDialog
        open={!!deleteTarget}
        title={t('deleteAccessPoint', 'Delete access point')}
        maxWidth='xs'
        message={t(
          'deleteAccessPointConfirm',
          'Are you sure you want to delete this access point? This cannot be undone.',
        )}
        primaryActionText={t('yesDelete', 'Yes, delete')}
        closeLabel={t('noCancel', 'No, cancel')}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />
    </>
  )
})
